import { IconDownload, IconEye, IconFile, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { documentosApi } from "@/features/documentos/services/documentos.api";
import { useDescargarDocumento, useEliminarDocumento } from "@/features/documentos/hooks/use-documentos-mutations";
import { esPrevisualizable, extensionDe } from "@/features/documentos/utils/documento-helpers";
import { Group, IconAction, Loader, Text, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui";
import { toastErrorApi } from "@/lib/toast";
import type { DocumentoVinculado } from "@/lib/types";
import type { DocumentoPreview } from "./DocumentoPreviewContent";
import { DocumentoPreviewModal } from "./DocumentoPreviewModal";

interface DocumentoRowProps {
  documento: DocumentoVinculado;
  solicitudId: string;
  puedeEliminar: boolean;
  /**
   * Modo compacto · oculta el nombre (se muestra en tooltip al pasar el mouse)
   * y deja solo las acciones. Usado en los ítems del checklist FB60.
   */
  compacto?: boolean;
}

/**
 * Fila de un documento ya persistido · botón previsualizar (PDF/imagen/XML)
 * + descargar (siempre) + eliminar (si puedeEliminar).
 *
 * El preview reusa el `DocumentoPreviewModal` que ya consume el detalle
 * de solicitud · soporta PDF, imágenes, XML/JSON/texto con highlighting.
 * Disponible para solicitante y autorizador por igual (read-only para este último).
 */
export function DocumentoRow({ documento, solicitudId, puedeEliminar, compacto = false }: DocumentoRowProps) {
  const descargar = useDescargarDocumento(solicitudId);
  const eliminar = useEliminarDocumento(solicitudId);

  const [preview, setPreview] = useState<DocumentoPreview | null>(null);
  const [opened, setOpened] = useState(false);

  const previsualizar = useMutation({
    mutationFn: () => documentosApi.previsualizar(solicitudId, documento.documentoId),
    onSuccess: (data) => {
      setPreview(data);
      setOpened(true);
    },
    onError: (err) => toastErrorApi(err),
  });

  const cerrarPreview = () => {
    setOpened(false);
    if (preview) {
      window.URL.revokeObjectURL(preview.blobUrl);
      setPreview(null);
    }
  };

  // Cleanup del blob URL al desmontar para no filtrar memoria del navegador.
  useEffect(() => {
    return () => {
      if (preview) window.URL.revokeObjectURL(preview.blobUrl);
    };
  }, [preview]);

  const ext = extensionDe(documento.nombreOriginal);
  const previewable = esPrevisualizable(ext);

  if (compacto) {
    // Solo las acciones (ver/descargar) · sin caja ni nombre inline · el nombre
    // se muestra en tooltip al pasar el mouse. Pensado para ir en una sola línea
    // junto a la etiqueta del ítem del checklist.
    return (
      <>
        <Tooltip>
          <TooltipTrigger asChild>
            <Group gap={2} wrap="nowrap">
              <IconAction label="Previsualizar" variant="subtle" size="sm" disabled={!previewable} loading={previsualizar.isPending} onClick={() => previsualizar.mutate()}>
                <IconEye size={14} stroke={1.7} />
              </IconAction>
              <IconAction
                label="Descargar"
                variant="subtle"
                size="sm"
                loading={descargar.isPending}
                onClick={() => descargar.mutate({ id: documento.documentoId, nombre: documento.nombreOriginal })}
              >
                <IconDownload size={14} stroke={1.7} />
              </IconAction>
            </Group>
          </TooltipTrigger>
          <TooltipContent className="max-w-85">{documento.nombreOriginal}</TooltipContent>
        </Tooltip>

        <DocumentoPreviewModal
          opened={opened}
          preview={preview}
          nombre={documento.nombreOriginal}
          descargando={descargar.isPending}
          onCerrar={cerrarPreview}
          onDescargar={() => descargar.mutate({ id: documento.documentoId, nombre: documento.nombreOriginal })}
        />
      </>
    );
  }

  return (
    <>
      <Group justify="space-between" wrap="nowrap" className="rounded-md border border-border p-2">
        <Group gap="xs" wrap="nowrap" className="min-w-0 flex-1">
          <IconFile size={14} stroke={1.7} />
          <div className="min-w-0 flex-1">
            <Text size="sm" truncate>
              {documento.nombreOriginal}
            </Text>
            {documento.tipoDocumento ? (
              // P31 · tipo del catálogo con el que se clasificó el adicional.
              <Text size="xs" c="dimmed" truncate title={documento.tipoDocumento}>
                {documento.tipoDocumento}
              </Text>
            ) : null}
          </div>
        </Group>
        <Group gap={4} wrap="nowrap">
          <IconAction
            label={previewable ? "Previsualizar" : "Sin vista previa"}
            variant="subtle"
            size="sm"
            disabled={!previewable}
            loading={previsualizar.isPending}
            onClick={() => previsualizar.mutate()}
          >
            <IconEye size={14} stroke={1.7} />
          </IconAction>
          <IconAction
            label="Descargar"
            variant="subtle"
            size="sm"
            loading={descargar.isPending}
            onClick={() => descargar.mutate({ id: documento.documentoId, nombre: documento.nombreOriginal })}
          >
            <IconDownload size={14} stroke={1.7} />
          </IconAction>
          {puedeEliminar && (
            <IconAction label="Eliminar" variant="subtle" size="sm" iconColor="peligro-600" loading={eliminar.isPending} onClick={() => eliminar.mutate(documento.documentoId)}>
              <IconTrash size={14} stroke={1.7} />
            </IconAction>
          )}
        </Group>
      </Group>

      <DocumentoPreviewModal
        opened={opened}
        preview={preview}
        nombre={documento.nombreOriginal}
        descargando={descargar.isPending}
        onCerrar={cerrarPreview}
        onDescargar={() => descargar.mutate({ id: documento.documentoId, nombre: documento.nombreOriginal })}
      />
    </>
  );
}

interface DocumentoPendienteRowProps {
  nombre: string;
}

/** Fila de documento que se está propagando vía outbox (badge "esperando"). */
export function DocumentoPendienteRow({ nombre }: DocumentoPendienteRowProps) {
  return (
    <Group gap="xs" wrap="nowrap" className="rounded-md border p-2" style={{ borderColor: "var(--color-info-300)", background: "color-mix(in srgb, var(--color-info-600) 8%, transparent)" }}>
      <Loader size="xs" color="multiva" />
      <Text size="sm" truncate className="flex-1">
        {nombre}
      </Text>
      <Text size="xs" c="info-700" tt="uppercase" fw={600}>
        esperando
      </Text>
    </Group>
  );
}
